import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search, Clock, TrendingUp, CheckCircle2, Sparkles, FileText, BarChart3, Zap, Database, Lock } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CoverageMapSection } from '../components/CoverageMapSection';
import { getIcon } from '../utils/iconMapper';
import { getFeatureCards, FeatureCard } from '../services/featureCardService';
import { getDashboardSettings } from '../services/settingsService';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardState, setDashboardState] = useState('idle');
  const [featureCards, setFeatureCards] = useState<FeatureCard[]>([]);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [showDmvConnections, setShowDmvConnections] = useState(true);
  const defaultSubtitle = 'Real-time DMV access, comprehensive vehicle analytics, and enterprise-grade compliance tools for the automotive industry.';
  const [homepageSubtitle, setHomepageSubtitle] = useState(defaultSubtitle);
  const defaultCoverageSubtitle = 'Comprehensive vehicle intelligence powered by direct integrations with DMVs and official NMVTIS data across all 50 states.';
  const [coverageMapSubtitle, setCoverageMapSubtitle] = useState(defaultCoverageSubtitle);

  useEffect(() => {
    const cycle = setInterval(() => {
      setDashboardState(current => {
        if (current === 'idle') return 'searching';
        if (current === 'searching') return 'results';
        return 'idle';
      });
    }, 3500);
    return () => clearInterval(cycle);
  }, []);

  useEffect(() => {
    // Fetch feature cards from API
    const fetchCards = async () => {
      try {
        const cards = await getFeatureCards();
        setFeatureCards(cards);
      } catch (error) {
        console.error('Failed to load feature cards:', error);
        // Keep empty array - component will gracefully handle no cards
      } finally {
        setIsLoadingCards(false);
      }
    };

    fetchCards();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getDashboardSettings();
        setShowDmvConnections(settings.showDmvConnections);
        if (settings.homepageSubtitle) {
          setHomepageSubtitle(settings.homepageSubtitle);
        }
        if (settings.coverageMapSubtitle) {
          setCoverageMapSubtitle(settings.coverageMapSubtitle);
        }
      } catch (error) {
        console.error('Failed to load dashboard settings:', error);
      }
    };

    fetchSettings();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-gray-800/20 via-gray-700/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-black via-gray-900/50 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] bg-gradient-to-br from-gray-900/10 to-gray-800/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <Navigation showDmvRecords={showDmvConnections} />

      {/* Hero Section */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-8 pt-28 md:pt-32 pb-40">
          {/* Top Row: Badge and NMVTIS Logo */}
          <div className="flex justify-end items-center mb-10">
            {/* NMVTIS Logo */}
            <img
              src="/nmvtis-logo.jpg"
              alt="NMVTIS - National Motor Vehicle Title Information System"
              className="h-16 w-auto rounded hidden sm:block"
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-10">
              {/* Headline */}
              <div className="space-y-8">
                <h1 className="text-7xl lg:text-8xl font-black leading-[0.9] tracking-tighter">
                  <span className="block">Vehicle Data</span>
                  <span className="block" style={{color: '#AD0000'}}>
                    Intelligence
                  </span>
                  <span className="block text-slate-700">Platform</span>
                </h1>

                <p className="text-xl text-slate-400 leading-relaxed max-w-xl">
                  {homepageSubtitle}
                </p>
              </div>

              {/* Feature Pills */}
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: Zap, label: 'Sub-second response' },
                  { icon: Database, label: '50-state coverage' },
                  { icon: Lock, label: 'SOC 2 certified' }
                ].map((item, i) => (
                  <div key={i} className="group flex items-center space-x-2 px-4 py-2.5 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-white/30 hover:-translate-y-0.5 transition-all duration-250 ease-spring cursor-pointer">
                    <item.icon className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors duration-250" />
                    <span className="text-sm font-semibold text-slate-300 group-hover:text-white transition-colors duration-250">{item.label}</span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/register')}
                  className="group px-8 py-4 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white font-bold rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg flex items-center space-x-3"
                >
                  <span>Get Started Now</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-250" />
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-6 pt-8 border-t border-white/10">
                {[
                  { value: '5K+', label: 'Clients' },
                  { value: '2.8M', label: 'Queries/mo' },
                  { value: '99.8%', label: 'Uptime' },
                  { value: '<1s', label: 'Response' }
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="text-3xl font-black text-white">
                      {stat.value}
                    </div>
                    <div className="text-xs text-slate-500 font-medium mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Dashboard Preview */}
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 to-gray-700/20 rounded-2xl blur-xl"></div>

              {/* Dashboard Card */}
              <div className="relative bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-slate-900/50">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  </div>
                  <div className="text-xs font-semibold text-slate-400">Dashboard Preview</div>
                  <div className="w-16"></div>
                </div>

                {/* Dashboard Content */}
                <div className="p-8 space-y-6">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      value={dashboardState === 'idle' ? '' : '1HGBH41JXMN109186'}
                      readOnly
                      placeholder="Enter VIN (e.g., 1HGBH41JXMN109186)"
                      className="w-full pl-12 pr-4 py-3.5 bg-slate-800/50 border border-white/10 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-[#AD0000]/50 transition-all font-mono text-sm"
                    />
                    {dashboardState === 'searching' && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <div className="w-5 h-5 border-2 border-[#AD0000] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>

                  {/* Idle State */}
                  {dashboardState === 'idle' && (
                    <div className="py-12 text-center space-y-3 opacity-0 animate-fade-in">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-700/20 to-gray-600/20 rounded-xl flex items-center justify-center mx-auto">
                        <FileText className="w-8 h-8 text-gray-300" />
                      </div>
                      <p className="text-slate-400 text-sm">
                        Enter a VIN to generate comprehensive vehicle report
                      </p>
                    </div>
                  )}

                  {/* Searching State */}
                  {dashboardState === 'searching' && (
                    <div className="py-12 text-center space-y-3 opacity-0 animate-fade-in">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-700/20 to-gray-600/20 rounded-xl flex items-center justify-center mx-auto">
                        <div className="w-8 h-8 border-3 border-[#AD0000] border-t-transparent rounded-full animate-spin"></div>
                      </div>
                      <p className="text-slate-400 text-sm">
                        Querying 50+ databases...
                      </p>
                    </div>
                  )}

                  {/* Results State */}
                  {dashboardState === 'results' && (
                    <div className="space-y-4 opacity-0 animate-fade-in">
                      {/* Vehicle Info Card */}
                      <div className="p-4 bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-lg border border-white/10">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="text-xs text-slate-400 mb-1">Vehicle Identified</div>
                            <div className="font-bold text-white text-lg">2021 Honda Accord</div>
                          </div>
                          <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-green-500/10 rounded border border-green-500/20">
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            <span className="text-xs font-semibold text-green-400">Verified</span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-slate-500">Year:</span>
                            <span className="text-white ml-2 font-medium">2021</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Make:</span>
                            <span className="text-white ml-2 font-medium">Honda</span>
                          </div>
                        </div>
                      </div>

                      {/* Status Checks Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { label: 'Title Status', status: 'Clean', icon: CheckCircle2, color: 'green' },
                          { label: 'Theft Check', status: 'Clear', icon: CheckCircle2, color: 'green' },
                          { label: 'Accident History', status: 'None', icon: CheckCircle2, color: 'green' },
                          { label: 'Odometer', status: 'Verified', icon: CheckCircle2, color: 'green' }
                        ].map((item, i) => (
                          <div key={i} className="p-3 bg-slate-800/30 rounded border border-white/5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs text-slate-400">{item.label}</span>
                              <item.icon className={`w-4 h-4 text-${item.color}-400`} />
                            </div>
                            <div className={`text-sm font-semibold text-${item.color}-400`}>{item.status}</div>
                          </div>
                        ))}
                      </div>

                      {/* View Full Report Button */}
                      <button className="w-full py-3.5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white text-sm font-bold rounded-lg transition-all duration-250 ease-spring shadow-lg shadow-red-500/20 hover:shadow-glow-red flex items-center justify-center space-x-2">
                        <FileText className="w-4 h-4" />
                        <span>View Full Report</span>
                      </button>
                    </div>
                  )}

                  {/* Live Indicator */}
                  <div className="flex items-center justify-between pt-4 border-t border-white/10">
                    <div className="flex items-center space-x-2">
                      <div className="relative">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <div className="absolute inset-0 w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
                      </div>
                      <span className="text-xs font-medium text-slate-400">
                        Live data • Verified
                      </span>
                    </div>
                    <div className="flex items-center space-x-1.5 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5" />
                      <span className="font-mono">287ms</span>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-3 bg-gradient-to-br from-gray-800/30 to-gray-700/20 rounded-lg border border-white/10">
                      <div className="text-2xl font-black text-white">&lt;1s</div>
                      <div className="text-xs text-slate-500 font-medium mt-1">Response</div>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-gray-800/30 to-gray-700/20 rounded-lg border border-white/10">
                      <div className="text-2xl font-black text-white">100%</div>
                      <div className="text-xs text-slate-500 font-medium mt-1">Accuracy</div>
                    </div>
                    <div className="text-center p-3 bg-gradient-to-br from-gray-800/30 to-gray-700/20 rounded-lg border border-white/10">
                      <div className="text-2xl font-black text-white">50+</div>
                      <div className="text-xs text-slate-500 font-medium mt-1">States</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -right-6 top-16 w-14 h-14 bg-gradient-to-br from-gray-700/20 to-gray-600/20 rounded-xl backdrop-blur-sm border border-white/20 p-3 animate-float">
                <BarChart3 className="w-full h-full text-white" />
              </div>
              <div className="absolute -left-6 bottom-16 w-14 h-14 bg-gradient-to-br from-gray-700/20 to-gray-600/20 rounded-xl backdrop-blur-sm border border-white/20 p-3 animate-float" style={{ animationDelay: '1s' }}>
                <TrendingUp className="w-full h-full text-gray-300" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="relative py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-black text-white mb-4">
              Enterprise-grade platform
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Everything you need to power automotive intelligence at scale
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {!isLoadingCards && featureCards.map((feature) => {
              const IconComponent = getIcon(feature.iconName);
              return (
                <div
                  key={feature.id}
                  className="group relative p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-white/30 hover:-translate-y-1 transition-all duration-300 ease-spring shadow-elevation-1 hover:shadow-elevation-3"
                >
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300"></div>
                  <div className={`w-14 h-14 bg-gradient-to-br from-${feature.gradientFrom} to-${feature.gradientTo} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 ease-spring shadow-lg`}>
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-black text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage Maps Section */}
      <CoverageMapSection showDmvConnections={showDmvConnections} subtitle={coverageMapSubtitle} />

      {/* NMVTIS Trust/Certification Section */}
      <section className="relative py-16">
        <div className="max-w-4xl mx-auto px-8">
          <div className="p-8 bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-white/10">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
              <img
                src="/nmvtis-logo.jpg"
                alt="NMVTIS"
                className="h-20 w-auto rounded-lg shadow-lg"
              />
              <div className="flex flex-col text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2">
                  Approved NMVTIS Data Provider
                </h3>
                <p className="text-sm text-slate-400 mb-1">
                  Auto Access Solutions
                </p>
                <p className="text-xs text-slate-500">
                  Official access to the National Motor Vehicle Title Information System
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-8">
            <Sparkles className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-slate-300">Get started today</span>
          </div>

          <h2 className="text-6xl font-black text-white mb-6">
            Ready to accelerate?
          </h2>
          <p className="text-xl text-slate-400 mb-12 max-w-2xl mx-auto">
            Join lenders and auction nationwide using AutoAccess Solutions for real-time vehicle intelligence.
          </p>
          <div className="flex items-center justify-center">
            <button
              onClick={() => navigate('/register')}
              className="px-10 py-5 bg-gradient-to-r from-[#AD0000] to-red-700 hover:from-red-700 hover:to-red-600 hover:scale-105 text-white font-bold text-lg rounded-xl transition-all duration-250 ease-spring shadow-xl shadow-red-500/30 hover:shadow-glow-red-lg"
            >
              Get Started Now
            </button>
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Home;